<template>
  <div id="app">
    <!-- Header -->
    <header class="site-header">
      <a href="#top" class="brandmark">MORIGNE</a>
      <nav class="nav">
        <a href="#oil">The oil</a>
        <a href="#place">The place</a>
        <a href="#family">The family</a>
        <a href="#contact">Acquire</a>
      </nav>
    </header>

    <main id="top">
      <!-- Hero -->
      <section class="hero">
        <div class="foil-logo" role="img" aria-label="Morigne, Extra Virgin Olive Oil"></div>
        <p class="hero-sub">The Curavić family · Morinje bay, Šibenik</p>
        <a href="#oil" class="scroll-cue" aria-label="Scroll">↓</a>
      </section>

      <!-- The oil -->
      <section id="oil" class="band">
        <div class="col reveal">
          <p class="eyebrow">Single-origin · Organic</p>
          <h2 class="display">Made to be worth giving.</h2>
          <p class="lead">A single-origin organic extra virgin olive oil from Morinje bay, on the Dalmatian
            coast. Picked and pressed cold within hours of the harvest, in limited quantity.</p>
        </div>
      </section>

      <!-- The place -->
      <section id="place" class="band alt">
        <div class="col reveal">
          <p class="eyebrow">The place</p>
          <h2 class="display">Morinje bay</h2>
          <img :src="bay" class="bay" alt="Outline of Morinje bay" />
          <p class="coords">43.6875° N&nbsp;&nbsp;·&nbsp;&nbsp;15.9468° E</p>
          <p class="body">A narrow bay set back from the open Adriatic. Our groves hold its two shores, east and
            west, low on the karst and close to the sea. Dalmatian summers are long and dry. They are hard on the
            tree, and they give the oil its intensity.</p>
        </div>
      </section>

      <!-- The family -->
      <section id="family" class="band">
        <div class="col reveal">
          <p class="eyebrow">The family</p>
          <h2 class="display">From Krapanj, by oar</h2>
          <p class="body">The family came from Krapanj, the smallest and lowest inhabited island in the Adriatic,
            a place of sponge divers. A century ago, before boats had engines, they rowed across to work these shores.
            The sea was the way to the land. The groves have passed down since, and in this generation turned wholly
            to the olive, worked by hand.</p>
        </div>
      </section>

      <!-- The method -->
      <section id="method" class="band alt">
        <div class="col reveal">
          <p class="eyebrow">The method</p>
          <h2 class="display">Tree to cellar</h2>
          <p class="body">Fine oil is lost in the hours after harvest. The fruit goes to a cold press the same day,
            and from there into a cellar kept against light, air and heat, where it rests until it is poured. Every
            pressing is measured, and each is meant to better the last.</p>
        </div>
      </section>

      <!-- Acquire -->
      <section id="contact" class="band">
        <div class="col reveal center">
          <p class="eyebrow">To acquire</p>
          <h2 class="display">For the few</h2>
          <p class="lead">We make little, and give away most of it. What remains is offered in small measure, by correspondence.</p>
          <a class="mail" href="mailto:evoo@morigne.com">evoo@morigne.com</a>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <p class="foot-brand">MORIGNE</p>
      <p class="foot-line">Extra virgin olive oil · The Curavić family · Šibenik, Dalmatia</p>
      <p class="foot-small">© {{ year }} Morigne</p>
    </footer>
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import bay from './assets/bay.svg'

const year = new Date().getFullYear()

onMounted(() => {
  const els = document.querySelectorAll('.reveal')
  if (!('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('in'))
    return
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } })
  }, { threshold: 0.18 })
  els.forEach(el => io.observe(el))
})
</script>

<style>
:root { --cream:#F6ECE3; --cream2:#EFE4D8; --clay:#C8A088; --terra:#B08968; --deep:#6E4F28; --ink:#1A140F; --soft:#8B7A69; }
* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; background: var(--cream); color: var(--ink);
  font-family: 'Montserrat', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }
#app { overflow-x: hidden; }
a { color: inherit; }
@media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } }
</style>

<style scoped>
/* Header */
.site-header { position: sticky; top: 0; z-index: 20; display: flex; align-items: center;
  justify-content: space-between; padding: 16px clamp(20px, 5vw, 56px);
  background: rgba(246, 236, 227, 0.82); backdrop-filter: blur(8px);
  border-bottom: 1px solid rgba(110, 79, 40, 0.12); }
.brandmark { font-family: 'Cormorant', serif; font-weight: 600; font-size: 20px;
  letter-spacing: 0.22em; color: var(--ink); text-decoration: none; }
.nav { display: flex; gap: clamp(16px, 3vw, 34px); }
.nav a { font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--soft);
  text-decoration: none; transition: color .3s; }
.nav a:hover { color: var(--terra); }
@media (max-width: 560px) { .nav { display: none; } }

/* Hero */
.hero { min-height: 92vh; display: flex; flex-direction: column; align-items: center; justify-content: center;
  text-align: center; padding: 40px 20px; position: relative;
  background: radial-gradient(120% 80% at 50% 12%, #FBF4EC, var(--cream) 65%); }
.foil-logo { width: min(360px, 66vw); aspect-ratio: 1258 / 734;
  -webkit-mask: url('./assets/morigne_logo.png') center / contain no-repeat;
          mask: url('./assets/morigne_logo.png') center / contain no-repeat;
  background: linear-gradient(115deg, #6E4F28 0%, #C6A46A 22%, #EAD3A0 40%, #9A7844 55%, #EAD3A0 70%, #6E4F28 100%);
  background-size: 200% 200%; }
.hero-sub { margin: 30px 0 0; font-family: 'Cormorant', serif; font-style: italic; font-size: 18px; color: var(--soft); }
.scroll-cue { position: absolute; bottom: 30px; font-size: 20px; color: var(--clay); text-decoration: none;
  animation: bob 2.4s ease-in-out infinite; }
@keyframes bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) { .scroll-cue { animation: none; } }

/* Bands */
.band { padding: clamp(72px, 12vw, 140px) clamp(20px, 5vw, 56px); }
.band.alt { background: var(--cream2); }
.col { max-width: 62ch; margin: 0 auto; }
.col.center { text-align: center; }
.eyebrow { margin: 0 0 18px; font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--clay); }
.display { font-family: 'Cormorant', serif; font-weight: 600; line-height: 1.05;
  font-size: clamp(34px, 6vw, 56px); margin: 0 0 26px; color: var(--ink); text-wrap: balance; }
.lead { font-size: clamp(17px, 2.4vw, 20px); line-height: 1.65; color: var(--terra); margin: 0; }
.body { font-size: 16px; line-height: 1.8; color: var(--ink); opacity: 0.85; margin: 0; }

/* Place graphic */
.bay { display: block; width: min(230px, 52vw); height: auto; margin: 8px 0 6px; opacity: 0.55; }
.coords { font-size: 12px; letter-spacing: 0.24em; color: var(--soft); margin: 0 0 30px;
  font-variant-numeric: tabular-nums; }

/* Contact */
.mail { display: inline-block; margin-top: 26px; font-family: 'Cormorant', serif; font-size: 26px;
  color: var(--deep); text-decoration: none; border-bottom: 1px solid rgba(110,79,40,0.3); padding-bottom: 3px;
  transition: color .3s, border-color .3s; }
.mail:hover { color: var(--terra); border-color: var(--terra); }

/* Footer */
.site-footer { text-align: center; padding: clamp(48px, 8vw, 80px) 20px; background: var(--ink); color: var(--cream); }
.foot-brand { font-family: 'Cormorant', serif; font-weight: 600; letter-spacing: 0.28em; font-size: 22px; margin: 0 0 12px; }
.foot-line { font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase; color: #cdbfae; margin: 0 0 10px; }
.foot-small { font-size: 11px; letter-spacing: 0.1em; color: #8f8272; margin: 0; }

/* Reveal on scroll */
.reveal { opacity: 0; transform: translateY(18px); transition: opacity .9s ease, transform .9s ease; }
.reveal.in { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) { .reveal { opacity: 1; transform: none; transition: none; } }
</style>
